import { NavLink, useLocation } from 'react-router-dom'
import { Shield, Car, Home, FileText, FolderOpen, LayoutDashboard, Building2, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'

const NAV = [
  { to: '/',          icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/vehicles',  icon: Car,             label: 'Vehicles'  },
  { to: '/home',      icon: Home,            label: 'Home'      },
  { to: '/property',  icon: Building2,       label: 'Property'  },
  { to: '/shares',    icon: TrendingUp,      label: 'Shares'    },
  { to: '/tax',       icon: FileText,        label: 'Tax'       },
  { to: '/insurance', icon: Shield,          label: 'Insurance' },
]

export default function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const title = NAV.find(n => n.to === pathname)?.label ?? 'Personal Hub'

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 truncate">{title}</h1>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto pb-20 md:pb-0 md:ml-56">
        <div className="max-w-2xl mx-auto px-4 py-4 md:py-6">
          {children}
        </div>
      </main>

      {/* Desktop sidebar */}
      <nav className="hidden md:flex fixed left-0 top-0 bottom-0 w-56 flex-col bg-white border-r border-slate-200 pt-4">
        <div className="px-4 pb-4 border-b border-slate-100">
          <span className="text-lg font-bold text-brand-600">Family Hub</span>
        </div>
        <div className="flex-1 py-3 space-y-0.5 overflow-auto">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-4 py-2.5 text-sm rounded-lg mx-2 transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </div>
        <div className="p-4 border-t border-slate-100">
          <NavLink
            to="/settings"
            className={({ isActive }) => cn(
              'flex items-center gap-2 text-sm px-2 py-2 rounded-lg transition-colors',
              isActive ? 'text-brand-700 font-medium' : 'text-slate-500 hover:text-slate-700'
            )}
          >
            Settings
          </NavLink>
        </div>
      </nav>

      {/* Mobile bottom nav — horizontally scrollable */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-10">
        <div className="flex overflow-x-auto scrollbar-hide">
          {NAV.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => cn(
                'flex-shrink-0 flex flex-col items-center gap-0.5 py-2 px-3 text-[10px] transition-colors min-w-[60px]',
                isActive ? 'text-brand-600' : 'text-slate-400'
              )}
            >
              <Icon size={20} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
